import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * initialTabFromUrl — read a `?tab=` deep-link hint once, on mount, so a
 * detail page can open on a specific tab (e.g. a mention notification links to
 * `/clients/:id?tab=notes` and lands the user right on the Notes thread).
 *
 * Returns the URL's tab when it is one of the page's known tabs, else the
 * page's fallback. Read once in a useState initializer — the value that matters
 * is the one present when the page mounts from the notification click.
 */
export function initialTabFromUrl<T extends string>(valid: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const t = new URLSearchParams(window.location.search).get('tab');
  return t && (valid as readonly string[]).includes(t) ? (t as T) : fallback;
}

/**
 * useTabState — `useState` for the active tab that ALSO records it in the URL's
 * `?tab=`, so that (7/28 N1) navigating out from a tab and pressing browser Back
 * returns you to the tab you left from — not the default Overview — system-wide on
 * every tabbed profile. A refresh or a deep link lands on the same tab too.
 *
 * The tab is written with `replace` (switching tabs adds no history entries), other
 * query params are preserved, and selecting the fallback tab clears `?tab=` so a
 * default view keeps a clean URL. Drop-in for `useState(() => initialTabFromUrl(...))`.
 */
export function useTabState<T extends string>(valid: readonly T[], fallback: T): [T, (t: T) => void] {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTabState] = useState<T>(() => initialTabFromUrl(valid, fallback));
  const setTab = useCallback((next: T) => {
    setTabState(next);
    // Sync `?tab=` onto the ROUTER's current location (not window.location, which a
    // MemoryRouter under test leaves at "/"), preserving any other query params.
    const params = new URLSearchParams(location.search);
    if (next === fallback) params.delete('tab'); else params.set('tab', next);
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [navigate, location.pathname, location.search, fallback]);
  return [tab, setTab];
}
